/**
 * ProgressSubHeader — línea única "hoy" debajo del header del Home v2:
 * minutos practicados hoy + estado del día + chevron a /progress, y — si el
 * coach tiene focos activos — la pill "en el radar" a su derecha, que
 * deep-linkea al tab Radar de /progress (plan 27 §9, pedido del dueño).
 *
 * Reemplaza a la grilla de 3 stats (racha · min hoy · frontera): la racha ya
 * vive en el StreakChip del header (era el mismo número dos veces a ~40px con
 * destinos distintos) y la cobertura de vocabulario tiene su casa en
 * Biblioteca (CoverageMeter) — "frontera" era jerga sin definición visible.
 * Una métrica accionable hoy, un destino (más el atajo al radar cuando hay).
 */
import { useEffect, useState } from 'react';
import { getReviewFoci } from '../../../lib/englishApi.js';
import { IcRadar } from '../../progress/radarIcons.js';
import { IconChevronRight, IconClock } from '../icons.js';
import type { Routine } from '../../../lib/englishApi.js';
import { isRestDay } from '../../../lib/routine.js';
import '../../../components/routine/routine.css';

/**
 * Focos ACTIVOS del radar para la pill del Home. `null` = sin dato (cargando
 * o error) → la pill no se muestra; no se inventa un 0. La fila del subheader
 * tiene alto fijo, así que la pill que llega async no mueve el layout (el
 * gate del hold-and-reveal protege ALTOS, no anchos).
 */
export function useRadarCount(): number | null {
  const [count, setCount] = useState<number | null>(null);
  useEffect(() => {
    let alive = true;
    getReviewFoci().then(
      (list) => {
        if (alive) setCount(list.active.length);
      },
      () => {
        /* sin dato: la home no muestra la pill, no rompe */
      },
    );
    return () => {
      alive = false;
    };
  }, []);
  return count;
}

interface ProgressSubHeaderProps {
  minutesToday: number;
  todayDone: boolean;
  /** Tap en la línea "hoy" → /progress. */
  onOpen: () => void;
  /** Focos activos del radar (`null` o 0 → la pill no se renderiza). */
  radarCount: number | null;
  /** Tap en la pill del radar → /progress/radar. */
  onOpenRadar: () => void;
  /**
   * «Tu rutina» (card #547): con rutina la línea muestra la meta del día
   * («6 de 10 min hoy · te faltan 4») y una barrita de progreso; en un día
   * libre lo dice. `null`/`undefined` = sin rutina (o cargando): como siempre.
   */
  routine?: Routine | null;
  now?: Date;
}

export function ProgressSubHeader({
  minutesToday,
  todayDone,
  onOpen,
  radarCount,
  onOpenRadar,
  routine,
  now,
}: ProgressSubHeaderProps) {
  const rest = routine != null && now != null && isRestDay(routine, now);
  // En un día libre no hay meta: lo que practique suma, pero nada «falta».
  const goal = routine && !rest ? routine.minutes : null;
  const met = goal !== null && minutesToday >= goal;
  let stateText = todayDone ? '· hoy ya cuenta' : '· la racha te espera';
  if (rest) stateText = todayDone ? '· día libre: suma igual' : '· hoy es libre';
  else if (goal !== null)
    stateText = met ? '· meta cumplida' : `· te faltan ${goal - minutesToday}`;
  // Sin aria-label a propósito: pisaría el contenido visible ("N min hoy · …")
  // en el accessible name y un lector de pantalla perdería el dato.
  return (
    <div className="h2-subhead-row">
      <button
        type="button"
        className={`h2-subhead h2-subhead--line${goal !== null ? ' has-goal' : ''}`}
        onClick={onOpen}
      >
        <span style={{ color: 'var(--cyan)', display: 'flex' }}>
          <IconClock size={14} />
        </span>
        <span className="h2-today-tx">
          <b>{minutesToday}</b> {goal !== null ? `de ${goal} ` : ''}min hoy
        </span>
        <span className={`h2-today-state${todayDone || met ? ' is-done' : ''}`}>{stateText}</span>
        {goal !== null && (
          <span
            className={`rt-subhead-bar${met ? ' is-met' : ''}`}
            style={{ width: `${Math.min(100, (minutesToday / goal) * 100)}%` }}
            aria-hidden="true"
          />
        )}
        <span className="h2-today-chev">
          <IconChevronRight size={14} />
        </span>
      </button>
      {radarCount !== null && radarCount > 0 && (
        <button type="button" className="h2-subhead h2-subhead--radar" onClick={onOpenRadar}>
          <span className="h2-radar-ic">
            <IcRadar size={13} />
          </span>
          <span className="h2-today-tx">
            <b>{radarCount}</b> en el radar
          </span>
        </button>
      )}
    </div>
  );
}
